import { Observable, of, Subscription } from 'rxjs';
import { ColumnDefinition } from './column-definition';
import { ListColumn } from './list-view-column';
import { isMoneyBackend, isMoneyLike, Money, MoneyLike, MoneyOptional } from './money';

export type ListValue<TValue = any, TValueOriginal = TValue> = {
  displayValue: TValue; // converted value (or original value if no converter defined)
  value: TValueOriginal; // original value
};

/**
 * TSource is the dataSource,
 * TValueOriginal is the type of the bound property on dataSource,
 * and TValue is TValueOriginal after (optional) conversion.
 */
export declare interface ListRow<TSource = any, TValue = any, TValueOriginal = TValue> {
  getValue(column: ListColumn<TSource, TValue, TValueOriginal>): TValue;
  getCalculatedValue(column: ListColumn<TSource, TValue, TValueOriginal>): number;
  dataSource: TSource;
  selected: boolean;
  addToSummaryValue(summaries: ListValue<TValue, TValueOriginal>[], column: ListColumn<TSource, TValue, TValueOriginal>);
  expanded?: boolean;
  highlight?: boolean;
}

export class ListViewRow<TSource = any, TValue = any, TValueOriginal = TValue> implements ListRow<TSource, TValue, TValueOriginal> {
  constructor(dataSource: TSource) {
    if (!dataSource && dataSource !== 0) {
      throw new Error('Invalid argument. Data source can not be null / undefined / empty.');
    }
    this.dataSource = dataSource;
  }

  private readonly _values: Map<string, ListValue<TValue, TValueOriginal>> = new Map();
  readonly dataSource: TSource;
  selected: boolean;
  highlight?: boolean;

  private getValueOriginal(dataSource: TSource, path: string): TValueOriginal {
    if (!path) {
      return dataSource as any;
    }
    const steps = path.split('.');
    let index = 0;
    let value: unknown = dataSource;
    while (index < steps.length) {
      if (value === undefined) {
        console.error(`Invalid data binding "${path}" (step ${index}) on object:`, dataSource);
        return undefined;
      }
      value = value[steps[index]];
      index++;
    }
    return value as TValueOriginal;
  }

  compareByColumn(row: ListViewRow<TSource, TValue, TValueOriginal>, column: ListColumn<TSource, TValue, TValueOriginal>): number {
    if (!row || !column) {
      throw new Error('Argument not specified.');
    }
    if (!column.comparer) {
      throw new Error('Comparer not specified.');
    }

    let thisVal: TValue | TValueOriginal;
    let thatVal: TValue | TValueOriginal;

    if (column.comparisonTarget == 'Original') {
      thisVal = this.getValueOriginal(this.dataSource, column.binding);
      thatVal = row.getValueOriginal(row.dataSource, column.binding);
    } else {
      thisVal = this.getValue(column);
      thatVal = row.getValue(column);
    }

    return column.comparer(thisVal, thatVal);
  }

  addToSummaryValue(summaries: ListValue<TValue, TValueOriginal>[], column: ListColumn<TSource, TValue, TValueOriginal>) {
    const value = this.getValueOriginal(this.dataSource, column.binding);

    if (isMoneyBackend(value)) {
      const valueBackend: MoneyLike = { amount: value.amount, currencyCode: value.currency.isoCode };
      this.sumMoney(summaries, valueBackend);
      return;
    }

    if (isMoneyLike(value)) {
      this.sumMoney(summaries, value);
      return;
    }

    if (isNaN(value as any)) {
      return;
    }

    if (summaries.length === 0) {
      summaries.push({ displayValue: undefined, value: 0 as TValueOriginal });
    }

    (summaries[0].value as number) += parseFloat(value as any);
  }

  private sumMoney(summaries: ListValue<TValue, TValueOriginal>[], value: MoneyLike): void {
    let summary = summaries.find(s => (s.value as Money).currencyCode === value.currencyCode) as ListValue<TValue, Money>;
    if (summary === undefined) {
      if (value instanceof MoneyOptional) {
        const valOpt = value as MoneyOptional;
        if (!valOpt.hasValue) return;
      }

      summary = { displayValue: undefined, value: new Money(value.currencyCode, 0) };
      summaries.push(summary as ListValue<TValue, TValueOriginal>);
    }
    summary.value.amount += value.amount;
  }

  getCalculatedValue(column: ListColumn<TSource, TValue, TValueOriginal>): number {
    const value = this.getValueOriginal(this.dataSource, column.binding);

    const amount = (value as MoneyLike)?.amount;
    if (amount !== undefined) {
      return parseFloat(amount as any);
    }

    if (isNaN(value as any)) {
      return 0;
    }

    return parseFloat(value as any);
  }

  private getValueSnapshot(column: ListColumn<TSource, TValue, TValueOriginal>): ListValue<TValue, TValueOriginal> {
    if (!column) {
      throw new Error('Column not specified.');
    }

    const key = column.binding;
    const value = this.getValueOriginal(this.dataSource, key);
    const cached = this._values.get(key);

    if (cached && cached.value === value) {
      // cached value exists and is up-to-date?
      return cached;
    }

    const snapshot = {
      displayValue: column.converter ? column.converter(value) : (value as unknown as TValue),
      value: value,
    };
    this._values.set(key, snapshot); // add to cache
    return snapshot;
  }

  getValue(column: ListColumn<TSource, TValue, TValueOriginal>): TValue {
    return this.getValueSnapshot(column).displayValue;
  }

  getHelpText(column: ListColumn<TSource, TValue, TValueOriginal>): string {
    if (column.helpBuilder) {
      return column.helpBuilder(this.dataSource);
    }
  }
}

export class ExpandedListViewRow<TSource = any, TItems = TSource> extends ListViewRow<TSource> {
  constructor(
    readonly caption: string,
    dataSource: TSource,
    readonly icon: string
  ) {
    super(dataSource);
  }

  columnDefinitions: ColumnDefinition<TSource>[];
  items: TItems[];
  dataLoader(dataContext: TSource): Observable<TItems[]> {
    return of(this.items);
  }

  private _expanded: boolean;
  loadSubscription: Subscription;
  get expanded(): boolean {
    return this._expanded;
  }
  set expanded(expanded: boolean) {
    if (!expanded) {
      this._expanded = expanded;
      return;
    }
    if (!this.items) {
      this.loadSubscription = this.dataLoader(this.dataSource).subscribe(result => {
        this.items = result;
        this._expanded = expanded;
      });
    } else {
      this._expanded = expanded;
    }
  }
}
